/**
 * Base API client.
 *
 * Сессия передаётся через cookie `frogstat_session`, которая ставится httpOnly
 * сервером в /api/auth/login. Браузер автоматически прикладывает её к каждому
 * запросу — никакой Authorization header добавлять не нужно.
 */
import { rememberResponseVersion, responseVersion } from "./response-version";
import { readTableResponse, saveTableResponse, touchTableResponse, clearTableResponses } from "./table-response-cache";
import { canPersistTableResponse, TABLE_RESPONSE_CACHE_IDENTITY_HEADER } from "./table-response-cache-policy";
import { currentPublicPageToken, publicPageDataUrl } from "@/lib/public-page-contract";

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Дефолтный таймаут запросов — 30 секунд. Большинство наших API-роутов
 * отвечают за миллисекунды, дёргают только локальный SQLite. Дольше 30 сек
 * может работать только refresh-пайплайн (и для него мы используем стримы,
 * не apiFetch). Если запрос завис — лучше упасть с понятной ошибкой,
 * чем держать висящий fetch вечно.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

export interface ApiFetchOptions extends RequestInit {
  /** Переопределение таймаута для конкретного запроса (мс). */
  timeoutMs?: number;
  /** Имеющийся объект SWR. 304 возвращает его же, не создавая новый массив. */
  previousData?: unknown;
  /** Только авторизованный user/account из StoreContext, не произвольный cache key. */
  responseCacheIdentity?: string;
}

export async function apiFetch<T = unknown>(
  url: string,
  options?: ApiFetchOptions,
): Promise<T> {
  const publicToken = currentPublicPageToken();
  if (publicToken && url.startsWith("/api/") && !url.startsWith("/api/shared/")) {
    return apiFetch<T>(publicPageDataUrl(publicToken, url), { ...options, credentials: "omit", responseCacheIdentity: undefined, previousData: undefined });
  }
  const { previousData, responseCacheIdentity, timeoutMs = DEFAULT_TIMEOUT_MS, ...requestOptions } = options ?? {};
  const headers = new Headers(requestOptions.headers);
  // FormData ставит свой Content-Type с границей частей — вручную его задавать нельзя.
  if (requestOptions.body && !(requestOptions.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const isGet = !requestOptions.method || requestOptions.method.toUpperCase() === "GET";
  const memoryEtag = isGet ? responseVersion(previousData, url) : undefined;

  // AbortController с таймаутом. Если у вызывающего уже есть signal, объединяем —
  // запрос отменяется по любому из источников.
  const ac = new AbortController();
  const timeoutId = setTimeout(() => ac.abort(new Error("Request timeout")), timeoutMs);

  const onAbort = () => ac.abort(requestOptions.signal?.reason);
  if (requestOptions.signal?.aborted) onAbort();
  else requestOptions.signal?.addEventListener("abort", onAbort, { once: true });

  try {
    const persistent = isGet && canPersistTableResponse(url, responseCacheIdentity);
    // Дисковое тело не показываем и даже не разбираем до авторизации/304.
    // Optimistic update без метки не заменяем старой дисковой копией.
    const snapshot = persistent ? await readTableResponse(url, responseCacheIdentity!, previousData === undefined) : null;
    const etag = memoryEtag ?? snapshot?.metadata?.etag;
    if (etag) headers.set("If-None-Match", etag);
    let res = await fetch(url, { ...requestOptions, headers, ...(etag ? { cache: "no-store" as const } : {}), signal: ac.signal });
    if (res.status === 304) {
      if (memoryEtag && previousData !== undefined) {
        if (snapshot) void touchTableResponse(snapshot, memoryEtag);
        return previousData as T;
      }
      if (snapshot?.body && snapshot.metadata && res.headers.get(TABLE_RESPONSE_CACHE_IDENTITY_HEADER) === responseCacheIdentity) {
        try {
          const data: unknown = await new Response(snapshot.body).json();
          if (data === null || typeof data !== "object") throw new Error("Invalid cached body");
          rememberResponseVersion(data, url, snapshot.metadata.etag);
          void touchTableResponse(snapshot, snapshot.metadata.etag);
          return data as T;
        } catch {
          // Повреждённый Blob — обычный GET, не ошибка всей страницы.
        }
      }
      // Нет локального тела — 304 использовать нельзя. Один полный повтор,
      // без вечного цикла при некорректном ответе прокси/сервера.
      headers.delete("If-None-Match");
      res = await fetch(url, { ...requestOptions, headers, cache: "no-store", signal: ac.signal });
    }
    if (persistent && [401, 403, 404].includes(res.status)) await clearTableResponses(responseCacheIdentity);
    // Читаем поток один раз, без Response.clone/tee и повторного stringify.
    // Blob хранит исходный JSON; живым объектом таблицы по-прежнему владеет SWR.
    const persistBody = !!snapshot && res.ok && res.headers.get(TABLE_RESPONSE_CACHE_IDENTITY_HEADER) === responseCacheIdentity && !!res.headers.get("etag");
    const blob = persistBody ? await res.blob() : null;
    let data: unknown = null;
    try {
      data = blob ? await new Response(blob).json() : await res.json();
    } catch {
      // Сервер вернул не-JSON (например, HTML страницу 502 от прокси).
      // Бросаем ApiError со статусом, а не упавший SyntaxError.
      throw new ApiError(res.status, `HTTP ${res.status} (некорректный JSON в ответе)`);
    }

    if (!res.ok) {
      const message = (data as { error?: string } | null)?.error ?? `HTTP ${res.status}`;
      throw new ApiError(res.status, message, data);
    }
    if (isGet) rememberResponseVersion(data, url, res.headers.get("etag"));
    if (snapshot && blob && data !== null && typeof data === "object") void saveTableResponse(snapshot, res.headers.get("etag")!, blob);
    return data as T;
  } catch (err) {
    // AbortError превращаем в понятное сообщение. Не проверяем через
    // `instanceof DOMException` — в Node.js-runtime (SSR/instrumentation)
    // DOMException может быть не определена, что даёт ReferenceError.
    // Name-based проверка работает и в браузере, и в Node.
    if (err && typeof err === "object" && (err as { name?: string }).name === "AbortError") {
      throw new ApiError(0, `Запрос отменён или превышен таймаут (${timeoutMs} мс)`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
    requestOptions.signal?.removeEventListener("abort", onAbort);
  }
}
